import { Component, inject, OnInit, ViewChild, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkStep, CdkStepper, CdkStepperModule } from '@angular/cdk/stepper';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  BulkPaymentRow,
  BulkPaymentMatch,
  BulkPaymentResult,
  BulkPaymentCsvData,
  ColumnMapping,
  MappingExportEnvelope,
  SavedColumnMapping,
} from '../../models/bulk-payment.model';
import { BulkPaymentService } from '../../services/bulk-payment.service';
import { BulkPaymentMappingService } from '../../services/bulk-payment-mapping.service';
import { PaymentCollectStore } from '../../store/payment-collect.store';
import { UserStore } from '../../../user/store/user.store';
import { CsvParseResult } from 'shared/services/csv-parser.service';
import { BulkPaymentUploadStepComponent } from './steps/upload-step.component';
import { BulkPaymentColumnMappingStepComponent } from './steps/column-mapping-step.component';
import { BulkPaymentReviewStepComponent } from './steps/review-step.component';
import { BulkPaymentProcessStepComponent } from './steps/process-step.component';
import { BulkPaymentResultsStepComponent } from './steps/results-step.component';
import { ModalService, ModalComponentInterface } from 'shared/components/modal/modal.service';
import { CustomersService } from '../../../customer/services/customers.service';
import { CustomerDetailsFormComponent } from '../../../customer/components/customer-details-form/customer-details-form.component';
import { CustomerSelectorModalComponent } from './modals/customer-selector-modal.component';
import { PaymentMethodSelectorModalComponent } from './modals/payment-method-selector-modal.component';
import { ExportMappingModalComponent } from './modals/export-mapping-modal.component';
import { ImportMappingModalComponent } from './modals/import-mapping-modal.component';
import { CustomerModel, PaymentMethodModel } from '../../../customer/customer.model';
import { NotificationService } from 'shared/services/notification.service';
import { CardDetailsFormComponent } from '../card-details-form/card-details-form.component';
import { CryptService } from 'shared/services/crypt.service';
import { TransactionsService } from '../../../transactions/services/transactions.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { CostOfCapitalGuardService } from 'app/services/cost-of-capital-guard.service';

export enum BulkPaymentStep {
  UPLOAD = 0,
  COLUMN_MAPPING = 1,
  REVIEW = 2,
  // Feature-flagged via `enableApplyCreditMemo` on ProfileAdminSettings.
  // When the flag is off the wizard should auto-skip this step.
  APPLY_CREDITS = 3,
  PROCESS = 4,
  RESULTS = 5,
}

@Component({
  selector: 'app-bulk-payment-wizard',
  standalone: true,
  imports: [
    CommonModule,
    CdkStepperModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    BulkPaymentUploadStepComponent,
    BulkPaymentColumnMappingStepComponent,
    BulkPaymentReviewStepComponent,
    BulkPaymentProcessStepComponent,
    BulkPaymentResultsStepComponent,
  ],
  templateUrl: './bulk-payment-wizard.component.html',
})
export class BulkPaymentWizardComponent implements OnInit {
  @ViewChild(CdkStepper) stepper!: CdkStepper;
  @ViewChild(BulkPaymentColumnMappingStepComponent) columnMappingStep?: BulkPaymentColumnMappingStepComponent;

  protected bulkPaymentService = inject(BulkPaymentService);
  protected bulkPaymentMappingService = inject(BulkPaymentMappingService);
  protected paymentCollectStore = inject(PaymentCollectStore);
  protected userStore = inject(UserStore);
  protected modalService = inject(ModalService);
  protected customersService = inject(CustomersService);
  protected notificationService = inject(NotificationService);
  protected cryptService = inject(CryptService);
  protected transactionsService = inject(TransactionsService);
  protected destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private cocGuard = inject(CostOfCapitalGuardService);

  // Data from modal wrapper
  data: any;

  // Wizard state
  currentStep = BulkPaymentStep.UPLOAD;
  BulkPaymentStep = BulkPaymentStep;

  // Cost of capital state
  applyCostOfCapital = false;
  costOfCapitalPercent = 0;

  // CSV data
  csvParseResult: CsvParseResult<BulkPaymentCsvData> | null = null;
  rawCsvData: string[][] = []; // Raw CSV data for column mapping
  // Remembers the uploaded file's display info so the upload step can
  // restore it after being destroyed/recreated by @if when navigating back.
  uploadedFileName: string | null = null;
  uploadedRowCount: number | null = null;
  columnMapping: ColumnMapping | null = null;
  bulkPaymentRows: BulkPaymentRow[] = [];

  // Matching results
  matches: BulkPaymentMatch[] = [];
  isMatching = false;

  // Processing results
  results: BulkPaymentResult[] = [];
  isProcessing = false;
  processingProgress = 0;

  // Step validation
  canProceedToColumnMapping = false;
  canProceedToReview = false;
  canProceedToProcess = false;
  canProceedToResults = false;

  // Saved column mapping for this profile
  savedMapping: SavedColumnMapping | null = null;
  isSavingMapping = false;

  get hasSavedMapping(): boolean {
    return this.savedMapping !== null;
  }

  ngOnInit(): void {
    // Get cost of capital percentage from profile settings
    const activeProfile = this.userStore.activeProfile();
    this.costOfCapitalPercent = activeProfile?.profileDetail?.profileAdminSettings?.costOfCapitalPercent ?? 0;

    // Retired customers are no longer loaded by default, and matching against a partial book
    // silently creates a duplicate for every retired account it fails to recognise. No-op once
    // the sweep has run. Loads the active book first if nothing has yet.
    this.paymentCollectStore.ensureCustomersLoaded({ includeInactive: true });
  }

  /**
   * Step 1: Handle CSV file upload and parse as raw data
   */
  onCsvUploaded(event: { fileName: string; rowCount: number; content: string }): void {
    // Parse CSV into raw string arrays
    this.rawCsvData = this.parseRawCsv(event.content);

    // Validate we have data
    this.canProceedToColumnMapping = this.rawCsvData.length > 0;
    this.uploadedFileName = event.fileName;
    this.uploadedRowCount = event.rowCount;
  }

  /**
   * Handle the uploaded file being cleared from the upload step. Without
   * this, "Next: Map Columns" would stay enabled after the file is removed,
   * since canProceedToColumnMapping is otherwise only set inside onCsvUploaded().
   */
  onCsvCleared(): void {
    this.rawCsvData = [];
    this.canProceedToColumnMapping = false;
    this.uploadedFileName = null;
    this.uploadedRowCount = null;
  }

  /**
   * Parse CSV file content into raw 2D array
   */
  private parseRawCsv(content: string): string[][] {
    const lines = content.split(/\r?\n/).filter((line) => line.trim() !== '');
    return lines.map((line) => this.parseCSVLine(line));
  }

  /**
   * Parse a single CSV line handling quotes and commas
   */
  private parseCSVLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  }

  /**
   * Step 2: Handle column mapping
   */
  onColumnMappingChange(mapping: ColumnMapping | null): void {
    this.columnMapping = mapping;
    this.canProceedToReview = mapping !== null;
  }

  /**
   * Proceed to column mapping step and fetch the profile's saved mapping.
   */
  proceedToColumnMapping(): void {
    if (!this.canProceedToColumnMapping) {
      return;
    }
    this.currentStep = BulkPaymentStep.COLUMN_MAPPING;
    this.cdr.detectChanges();

    // Load saved mapping in the background; no auto-apply on entry.
    this.bulkPaymentMappingService.getSavedMapping().subscribe({
      next: (mapping) => {
        this.savedMapping = mapping;
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Save the current column mapping for this profile.
   * Overwrites any previously saved mapping.
   */
  saveColumnMapping(): void {
    const toSave = this.columnMappingStep?.getMappingForSave();
    if (!toSave) {
      return;
    }

    this.isSavingMapping = true;
    this.cdr.detectChanges();

    this.bulkPaymentMappingService.saveMapping(toSave).subscribe({
      next: (saved) => {
        this.savedMapping = saved;
        this.isSavingMapping = false;
        if (saved) {
          this.notificationService.success('Column mapping saved');
        } else {
          this.notificationService.error('Failed to save column mapping');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isSavingMapping = false;
        this.notificationService.error('Failed to save column mapping');
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Apply the profile's saved column mapping to the current CSV.
   */
  applySavedMapping(): void {
    if (!this.savedMapping || !this.columnMappingStep) {
      return;
    }
    this.columnMappingStep.applySavedMapping(this.savedMapping);
  }

  /**
   * Open the Export modal: fetch all saved versions, let the user pick one,
   * then download the selected version as a JSON envelope file.
   */
  openExportMapping(): void {
    this.bulkPaymentMappingService.listVersions().subscribe({
      next: (versions) => {
        if (!versions || versions.length === 0) {
          this.notificationService.error('No saved mappings to export. Save a mapping first.');
          return;
        }
        this.modalService.openModal({
          title: 'Export Column Mapping',
          body: ExportMappingModalComponent,
          size: 'md' as any,
          data: { versions, mappingService: this.bulkPaymentMappingService },
          dismiss: { label: 'Cancel', action: (modal: any) => modal.close() },
          confirm: {
            label: 'Download',
            action: (modal: any, component: ExportMappingModalComponent) => {
              const envelope = component.getSelectedEnvelope();
              if (!envelope) return;
              this.downloadJson(envelope, `bulk-payment-mapping_v${envelope.version}.json`);
              modal.close();
            },
          },
        });
      },
    });
  }

  /**
   * Open the Import modal: let the user upload a JSON envelope file,
   * validate it, then save it as a new version for the current profile.
   */
  openImportMapping(): void {
    this.modalService.openModal({
      title: 'Import Column Mapping',
      body: ImportMappingModalComponent,
      size: 'md' as any,
      data: {},
      dismiss: { label: 'Cancel', action: (modal: any) => modal.close() },
      confirm: {
        label: 'Import',
        action: (modal: any, component: ImportMappingModalComponent) => {
          if (!component.isValid || !component.parsedMapping) return;
          this.bulkPaymentMappingService.saveMapping(component.parsedMapping).subscribe({
            next: (saved) => {
              if (saved) {
                this.savedMapping = saved;
                this.notificationService.success('Mapping imported and saved as a new version.');
              } else {
                this.notificationService.error('Failed to import mapping.');
              }
              this.cdr.detectChanges();
            },
            error: () => {
              this.notificationService.error('Failed to import mapping.');
            },
          });
          modal.close();
        },
      },
      additionalButtons: [
        {
          label: 'Download Sample',
          class: 'mat-secondary download-sample-btn',
          closeOnClick: false,
          action: () => {
            const sample: MappingExportEnvelope = {
              mappingType: 'BULK_PAYMENT_COLUMN',
              mappingName: 'default',
              version: 1,
              exportedAt: new Date().toISOString(),
              mapping: {
                hasHeaders: true,
                accountNumberColumn: { index: 0, header: 'Account Number' },
                amountColumn: { index: 1, header: 'Amount' },
                invoiceNumberColumn: { index: 2, header: 'Invoice Number' },
              },
            };
            this.downloadJson(sample, 'bulk-payment-mapping-sample.json');
          },
        },
      ],
    });
  }

  /** Download an object as a pretty-printed JSON file. */
  private downloadJson(obj: object, filename: string): void {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Handle cost of capital toggle change
   */
  onCostOfCapitalChange(enabled: boolean): void {
    this.applyCostOfCapital = enabled;
    this.updateCostOfCapital();
  }

  /**
   * Update cost of capital amounts in bulk payment rows
   */
  private updateCostOfCapital(): void {
    if (this.applyCostOfCapital && this.costOfCapitalPercent > 0) {
      this.bulkPaymentRows.forEach((row) => {
        row.costOfCapitalAmount = row.amount * (this.costOfCapitalPercent / 100);
      });
    } else {
      this.bulkPaymentRows.forEach((row) => {
        row.costOfCapitalAmount = 0;
      });
    }
    this.cdr.detectChanges();
  }

  /**
   * Step 3: Apply column mapping and proceed to review
   */
  proceedToReview(): void {
    if (!this.canProceedToReview || !this.columnMapping) {
      console.warn('Cannot proceed to review: validation failed');
      return;
    }

    // Apply column mapping to create bulk payment rows
    this.bulkPaymentRows = this.applyColumnMapping(this.rawCsvData, this.columnMapping);

    // Apply cost of capital if enabled
    this.updateCostOfCapital();

    this.isMatching = true;
    this.currentStep = BulkPaymentStep.REVIEW;
    this.cdr.detectChanges();

    try {
      const customers = this.paymentCollectStore.data.customers();
      console.log('Number of customers available:', customers?.length || 0);
      console.log('Number of rows to match:', this.bulkPaymentRows.length);

      // Match customers
      this.bulkPaymentService.matchCustomers(this.bulkPaymentRows, customers || []).subscribe({
        next: (matches) => {
          console.log('Matching complete. Results:', matches);
          this.matches = matches;
          this.isMatching = false;

          // Can proceed only when at least one row has a matched customer + valid amount + valid invoice
          this.canProceedToProcess = matches.some(
            (m) => m.status === 'matched' && m.row.isAmountValid && m.row.isInvoiceValid
          );
          console.log('Can proceed to process:', this.canProceedToProcess);

          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error matching customers:', error);
          this.isMatching = false;
          this.cdr.detectChanges();
        },
      });
    } catch (error) {
      console.error('Error in proceedToReview:', error);
      this.isMatching = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Validate an amount string from CSV or user edit.
   * Strips currency symbols/spaces/commas first, then requires a clean
   * decimal-only pattern so "1.2.3" or "abc" are caught as invalid.
   */
  parseAndValidateAmount(raw: string): { amount: number; isValid: boolean } {
    const stripped = (raw || '').replace(/[^0-9.\-]/g, '');
    const isNumericShape = /^\d+(\.\d+)?$/.test(stripped);
    const n = parseFloat(stripped);
    const isValid = isNumericShape && !isNaN(n) && n > 0;
    return { amount: isValid ? n : 0, isValid };
  }

  /**
   * Apply column mapping to raw CSV data.
   * Every non-empty row is kept; invalid amounts are flagged rather than dropped.
   */
  private applyColumnMapping(rawData: string[][], mapping: ColumnMapping): BulkPaymentRow[] {
    const dataStartIndex = mapping.hasHeaders ? 1 : 0;
    const rows: BulkPaymentRow[] = [];

    for (let i = dataStartIndex; i < rawData.length; i++) {
      const row = rawData[i];

      // Skip completely empty lines
      if (row.every((cell) => !cell.trim())) continue;

      // Get account number (may be empty — flagged in matchCustomers)
      const accountNumber = row[mapping.accountNumberColumn]?.trim() || '';

      // Get and validate amount — keep row regardless of validity
      const rawAmount = row[mapping.amountColumn] || '';
      const { amount, isValid: isAmountValid } = this.parseAndValidateAmount(rawAmount);

      // Get invoice number (rest of line from this column)
      let rawInvoiceNumber = '';
      if (mapping.invoiceNumberColumn !== undefined && mapping.invoiceNumberColumn >= 0) {
        const remainingValues = row.slice(mapping.invoiceNumberColumn);
        rawInvoiceNumber = remainingValues.join(',');
      }
      const trimmedInvoice = rawInvoiceNumber.trim();
      const isInvoiceValid = trimmedInvoice.length > 0;

      rows.push({
        accountNumber,
        amount,
        rawAmount: rawAmount.trim(),
        isAmountValid,
        invoiceNumber: isInvoiceValid ? trimmedInvoice : undefined,
        rawInvoiceNumber,
        isInvoiceValid,
        rowIndex: i + 1,
      });
    }

    return rows;
  }

  /**
   * Step 4: Process bulk payments
   */
  async proceedToProcess(): Promise<void> {
    if (!this.canProceedToProcess) {
      return;
    }

    // Zero out CoC for any match whose card billing state is blocked
    await Promise.all(
      this.matches.map(async (match) => {
        const cardZip = match.paymentMethod?.zipCode;
        if (match.row.costOfCapitalAmount && match.row.costOfCapitalAmount > 0 && cardZip) {
          const blocked = await this.cocGuard.isBlocked(cardZip);
          if (blocked) {
            match.row.costOfCapitalAmount = 0;
          }
        }
      })
    );

    this.isProcessing = true;
    this.processingProgress = 0;
    this.currentStep = BulkPaymentStep.PROCESS;
    this.cdr.detectChanges();

    // Process payments
    this.bulkPaymentService.processBulkPayments(this.matches).subscribe({
      next: (results) => {
        this.results = results;
        this.isProcessing = false;
        this.processingProgress = 100;
        this.canProceedToResults = true;
        this.cdr.detectChanges();

        // Auto-advance to results
        setTimeout(() => {
          this.proceedToResults();
        }, 500);
      },
      error: (error) => {
        console.error('Error processing payments:', error);
        this.isProcessing = false;
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Step 5: Show results
   */
  proceedToResults(): void {
    this.currentStep = BulkPaymentStep.RESULTS;
    this.cdr.detectChanges();
  }

  /**
   * Download results as CSV
   */
  downloadResults(): void {
    const csvContent = this.bulkPaymentService.generateResultsCsv(this.matches, this.results);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    this.bulkPaymentService.downloadCsv(csvContent, `bulk-payment-results-${timestamp}.csv`);
  }

  /**
   * View receipt for a transaction
   */
  viewReceipt(transactionId: string): void {
    this.transactionsService
      .getTransaction(transactionId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((transactionDetails) => this.transactionsService.getHTMLReceipt(transactionDetails))
      )
      .subscribe({
        next: (htmlContent) => {
          const newWindow = window.open(
            ' ',
            '_blank',
            'width=500,height=700,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes,titlebar=no'
          );
          if (newWindow) {
            newWindow.document.write(htmlContent);
            newWindow.document.close();
          }
        },
        error: (error) => {
          console.error('Error viewing receipt:', error);
          this.notificationService.error('Failed to load receipt');
        },
      });
  }

  /**
   * Download receipt for a transaction as PDF
   */
  downloadReceipt(transactionId: string): void {
    this.transactionsService
      .getTransaction(transactionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (transactionDetails) => {
          this.transactionsService.getPdfReceipt(transactionDetails);
        },
        error: (error) => {
          console.error('Error downloading receipt:', error);
          this.notificationService.error('Failed to download receipt');
        },
      });
  }

  /**
   * Handle add customer for unmatched row
   */
  onAddCustomer(match: BulkPaymentMatch): void {
    this.modalService.openModal({
      title: 'Add New Customer',
      body: CustomerDetailsFormComponent,
      data: {
        customerData: {
          companyAccountNumber: match.row.accountNumber,
        },
      },
      dismiss: {
        label: 'Cancel',
        action: (modal: any) => {
          modal.close();
        },
      },
      confirm: {
        label: 'Save Customer',
        action: (modal, customerFormComponent: CustomerDetailsFormComponent) => {
          const form = customerFormComponent.form;
          if (!form.valid) {
            form.markAllAsTouched();
            return;
          }

          const formData = form.getRawValue();
          const customerModel: CustomerModel = {
            contactFirstName: formData!.contactFirstName ?? undefined,
            contactLastName: formData!.contactLastName ?? undefined,
            displayName: formData!.displayName ?? undefined,
            companyName: formData!.companyName ?? undefined,
            companyAccountNumber: formData!.companyAccountNumber ?? undefined,
            contactEmail: formData!.contactEmail ?? undefined,
            contactPhoneNumber: formData!.businessPhone + ' ' + formData!.businessPhoneExt,
            mobilePhoneNumber: formData!.mobilePhoneNumber ?? undefined,
            notes: formData!.notes ?? undefined,
          };

          // Clean up empty fields
          if (!customerModel.contactEmail) delete customerModel.contactEmail;
          if (customerModel.contactEmail == '') delete customerModel.contactEmail;
          if (customerModel.mobilePhoneNumber == ' ') delete customerModel.mobilePhoneNumber;
          if (customerModel.contactPhoneNumber == ' ') delete customerModel.contactPhoneNumber;
          if (customerModel.mobilePhoneNumber == '') delete customerModel.mobilePhoneNumber;
          if (customerModel.contactPhoneNumber == '') delete customerModel.contactPhoneNumber;
          if (customerModel.contactFirstName == '') delete customerModel.contactFirstName;
          if (customerModel.contactLastName == '') delete customerModel.contactLastName;

          this.customersService.createCustomer(customerModel).subscribe({
            next: (newCustomer) => {
              // Show success notification
              this.notificationService.success('Customer created successfully');

              // Reload customers
              this.paymentCollectStore.loadCustomers();

              // Update the match with the new customer and fetch payment methods
              this.updateMatchWithCustomer(match, newCustomer);

              modal.close();
            },
            error: (error) => {
              console.error('Error creating customer:', error);
              this.notificationService.error('Failed to create customer');
            },
          });
        },
      },
    });
  }

  /**
   * Handle select customer for unmatched row
   */
  onSelectCustomer(match: BulkPaymentMatch): void {
    const customers = this.paymentCollectStore.data.customers();

    this.modalService.openModal({
      title: 'Select Customer',
      body: CustomerSelectorModalComponent,
      data: {
        match,
        customers: customers || [],
      },
      dismiss: {
        label: 'Cancel',
        action: (modal: any) => {
          modal.close();
        },
      },
      confirm: {
        label: 'Select Customer',
        action: (modal, selectorComponent: CustomerSelectorModalComponent) => {
          if (!selectorComponent.selectedCustomer) {
            return;
          }

          this.updateMatchWithCustomer(match, selectorComponent.selectedCustomer);
          modal.close();
        },
      },
    });
  }

  /**
   * Update match with selected customer and fetch payment methods
   */
  private updateMatchWithCustomer(match: BulkPaymentMatch, customer: CustomerModel): void {
    match.customer = customer;

    // Ensure customer has an id
    if (!customer.id) {
      match.status = 'no-payment-method';
      match.error = 'Customer ID is missing';
      this.cdr.detectChanges();
      return;
    }

    // Fetch payment methods for the customer
    this.bulkPaymentService['getDefaultPaymentMethod'](customer.id).subscribe({
      next: (paymentMethod) => {
        if (paymentMethod) {
          match.paymentMethod = paymentMethod;
          match.status = 'matched';
          match.error = undefined;
        } else {
          match.status = 'no-payment-method';
          match.error = 'No payment method found';
        }

        this.recomputeCanProceedToProcess();
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error fetching payment methods:', error);
        match.status = 'no-payment-method';
        match.error = 'Failed to load payment methods';
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Handle inline amount edit from review step.
   * Re-validates the new raw value, mutates the row in-place,
   * then recomputes COC and the proceed gate.
   */
  onAmountEdited(match: BulkPaymentMatch, newRawAmount: string): void {
    const { amount, isValid } = this.parseAndValidateAmount(newRawAmount);
    match.row.rawAmount = newRawAmount.trim();
    match.row.amount = amount;
    match.row.isAmountValid = isValid;
    this.updateCostOfCapital();
    this.recomputeCanProceedToProcess();
    this.cdr.detectChanges();
  }

  /**
   * Handle inline invoice edit from review step.
   * The server's `SimpleSaleRequest` Joi schema rejects an empty `reference` with 400,
   * so we treat blank invoice as a row-level issue that the merchant must resolve before processing.
   */
  onInvoiceEdited(match: BulkPaymentMatch, newRawInvoice: string): void {
    const trimmed = (newRawInvoice ?? '').trim();
    const isValid = trimmed.length > 0;
    match.row.rawInvoiceNumber = newRawInvoice ?? '';
    match.row.invoiceNumber = isValid ? trimmed : undefined;
    match.row.isInvoiceValid = isValid;
    this.recomputeCanProceedToProcess();
    this.cdr.detectChanges();
  }

  /**
   * Single source of truth for the "Process Payments" gate.
   * A row is processable when its customer is matched, payment method is set,
   * amount is valid AND invoice is non-blank (server requires `reference`).
   */
  private recomputeCanProceedToProcess(): void {
    this.canProceedToProcess = this.matches.some(
      (m) => m.status === 'matched' && m.row.isAmountValid && m.row.isInvoiceValid
    );
  }

  /**
   * Handle select payment method for matched customer without payment method
   */
  onSelectPaymentMethod(match: BulkPaymentMatch): void {
    if (!match.customer?.id) {
      return;
    }

    // Fetch all payment methods for the customer
    const cardMethods$ = this.customersService.getPaymentMethods(match.customer.id);
    const achMethods$ = this.customersService.getAchPaymentMethods(match.customer.id);

    forkJoin({ cardMethods: cardMethods$, achMethods: achMethods$ }).subscribe({
      next: ({ cardMethods, achMethods }) => {
        const allMethods = [...cardMethods, ...achMethods];

        this.modalService.openModal({
          title: 'Select Payment Method',
          body: PaymentMethodSelectorModalComponent,
          data: {
            match,
            paymentMethods: allMethods,
          },
          dismiss: {
            label: 'Cancel',
            action: (modal: any) => {
              modal.close();
            },
          },
          confirm: {
            label: 'Select Payment Method',
            action: (modal, selectorComponent: PaymentMethodSelectorModalComponent) => {
              if (!selectorComponent.selectedPaymentMethod) {
                return;
              }

              // Update the match
              match.paymentMethod = selectorComponent.selectedPaymentMethod;
              match.status = 'matched';
              match.error = undefined;

              this.recomputeCanProceedToProcess();
              this.cdr.detectChanges();

              modal.close();
            },
          },
        });
      },
      error: (error) => {
        console.error('Error fetching payment methods:', error);
        this.notificationService.error('Failed to load payment methods');
      },
    });
  }

  /**
   * Handle add card for customer
   */
  onAddCard(match: BulkPaymentMatch): void {
    if (!match.customer?.id) {
      this.notificationService.error('Customer ID is missing');
      return;
    }

    const customerId = match.customer.id;

    this.modalService.openModal({
      title: 'Add Payment Card',
      body: CardDetailsFormComponent,
      data: {},
      dismiss: {
        label: 'Cancel',
        action: (modal: any) => {
          modal.close();
        },
      },
      confirm: {
        label: 'Save',
        action: (modal: ModalComponentInterface, cardDetailsFormComponent: CardDetailsFormComponent) => {
          const form = cardDetailsFormComponent.form;
          if (!form.valid) {
            form.markAllAsTouched();
            return;
          }

          const paymentMethodModel = cardDetailsFormComponent.getFormData();

          this.cryptService
            .encryptData(paymentMethodModel)
            .then((encryptedPaymentMethod) => {
              const encryptedPaymentMethodModel: PaymentMethodModel = {
                encryptedPaymentAccount: encryptedPaymentMethod,
              };

              this.customersService.createPaymentMethod(customerId, encryptedPaymentMethodModel).subscribe({
                next: (newPaymentMethod) => {
                  this.notificationService.success('Payment card added successfully');

                  // Update the match with the new payment method
                  match.paymentMethod = newPaymentMethod;
                  match.status = 'matched';
                  match.error = undefined;

                  this.recomputeCanProceedToProcess();
                  this.cdr.detectChanges();

                  modal.close();
                },
                error: (error) => {
                  console.error('Error creating payment method:', error);
                  this.notificationService.error('Failed to add payment card');
                },
              });
            })
            .catch((error) => {
              console.error('Error encrypting payment data:', error);
              this.notificationService.error('Failed to encrypt payment data');
            });
        },
      },
    });
  }

  /**
   * Go back to previous step
   */
  goBack(): void {
    if (this.currentStep > BulkPaymentStep.UPLOAD) {
      this.currentStep--;
    }
  }

  /**
   * Reset wizard to start over
   */
  reset(): void {
    this.currentStep = BulkPaymentStep.UPLOAD;
    this.csvParseResult = null;
    this.rawCsvData = [];
    this.uploadedFileName = null;
    this.uploadedRowCount = null;
    this.columnMapping = null;
    this.bulkPaymentRows = [];
    this.matches = [];
    this.results = [];
    this.canProceedToColumnMapping = false;
    this.canProceedToReview = false;
    this.canProceedToProcess = false;
    this.canProceedToResults = false;
    this.savedMapping = null;
    this.isSavingMapping = false;
  }

  /**
   * Get counts for display
   */
  get matchedCount(): number {
    return this.matches.filter((m) => m.status === 'matched' && m.row.isAmountValid && m.row.isInvoiceValid).length;
  }

  get unmatchedCount(): number {
    return this.matches.filter((m) => m.status !== 'matched').length;
  }

  get successCount(): number {
    return this.results.filter((r) => r.status === 'success').length;
  }

  get failedCount(): number {
    return this.results.filter((r) => r.status === 'failed').length;
  }
}
